import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardActions,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Box,
  IconButton,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getTLCDetail,
  updateTLC,
  updateTLCMember,
  createTLCMember,
  deleteTLCMember,
  replaceTLCMember,
  createTLCChapter,
  deleteTLCChapter,
} from '../../endpoints/tlc';
import {
  TLCDetailResponse,
  TLCPatchRequest,
  TLCMemberPostRequest,
  TLCMemberPatchRequest,
  TLCMemberReplaceRequest,
  TLCChapterPostRequest,
} from '../../types/tlc';
import PositionDisplay from '../../Components/PositionDisplay';
import { Cancel, Save, Edit, Add, Delete } from '@mui/icons-material';
import { roles } from '../../utils/variables';
import UserSearchModal from '../../Components/UserSearchModal';
import DateFilter from '../../Components/DateFilter';
import ChapterSelector from '../../Components/ChapterSelector';
import dayjs, { Dayjs } from 'dayjs';

interface User {
  id: string;
  first_name: string;
  last_name: string;
}

interface SaveData {
  id: string;
  positionId?: string;
  committeeId?: string;
  action: 'newAppointment' | 'editDates' | 'removeRecord';
  userId?: string;
  startDate?: string | null;
  endDate?: string | null;
}

const TLCDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorSaving, setErrorSaving] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [tlcError, setTlcError] = useState<string | null>(null);
  const [name, setName] = useState<string>('');
  const [status, setStatus] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const [memberError, setMemberError] = useState<string | null>(null);
  const [addMember, setAddMember] = useState<boolean>(false);
  const [member, setMember] = useState<User | null>(null);
  const [isPrimary, setIsPrimary] = useState<boolean>(false);
  const [openMemberSearch, setOpenMemberSearch] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());

  // Chapter management state
  const [addChapterModalOpen, setAddChapterModalOpen] =
    useState<boolean>(false);
  const [selectedChapterId, setSelectedChapterId] = useState<string>('');
  const [chapterError, setChapterError] = useState<string | null>(null);
  const [isSubmittingChapter, setIsSubmittingChapter] =
    useState<boolean>(false);

  const {
    data: tlcData,
    isLoading,
    refetch,
  } = useApiGet<TLCDetailResponse, string>(
    ['tlc-detail', id!],
    getTLCDetail,
    {
      enabled: !!id,
      retry: 1,
    },
    id!
  );

  const { mutate: updateTLCMutation } = useApiSend<
    { message: string },
    { id: string; data: TLCPatchRequest }
  >(
    ({ id, data }) => updateTLC(id, data),
    () => {
      setIsSaving(false);
      setIsEditing(false);
      setTlcError(null);
      refetch();
    },
    (error: Error) => {
      setTlcError(
        error.message ||
          'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: updateTLCMemberMutation } = useApiSend<
    { message: string },
    { tlcId: string; memberId: string; data: TLCMemberPatchRequest }
  >(
    ({ tlcId, memberId, data }) => updateTLCMember(tlcId, memberId, data),
    () => {
      setIsSaving(false);
      setMemberError(null);
      refetch();
    },
    (error: Error) => {
      setMemberError(
        error.message ||
          'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: addTLCMemberMutation } = useApiSend<
    { message: string },
    { tlcId: string; data: TLCMemberPostRequest }
  >(
    ({ tlcId, data }) => createTLCMember(tlcId, data),
    () => {
      setIsSaving(false);
      setMemberError(null);
      setAddMember(false);
      setMember(null);
      setIsPrimary(false);
      setStartDate(dayjs());
      refetch();
    },
    (error: Error) => {
      setMemberError(
        error.message ||
          'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: removeTLCMemberMutation } = useApiSend<
    { message: string },
    { tlcId: string; memberId: string }
  >(
    ({ tlcId, memberId }) => deleteTLCMember(tlcId, memberId),
    () => {
      setIsSaving(false);
      setMemberError(null);
      refetch();
    },
    (error: Error) => {
      setMemberError(
        error.message ||
          'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: replaceMemberMutation } = useApiSend<
    { message: string },
    { tlcId: string; memberId: string; data: TLCMemberReplaceRequest }
  >(
    ({ tlcId, memberId, data }) => replaceTLCMember(tlcId, memberId, data),
    () => {
      setIsSaving(false);
      setMemberError(null);
      refetch();
    },
    (error: Error) => {
      setMemberError(
        error.message ||
          'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: addTLCChapterMutation } = useApiSend<
    { message: string },
    { tlcId: string; data: TLCChapterPostRequest }
  >(
    ({ tlcId, data }) => createTLCChapter(tlcId, data),
    () => {
      setIsSubmittingChapter(false);
      setChapterError(null);
      setAddChapterModalOpen(false);
      setSelectedChapterId('');
      refetch();
    },
    (error: Error) => {
      setChapterError(error.message || 'Failed to add chapter');
      setIsSubmittingChapter(false);
    }
  );

  const { mutate: deleteTLCChapterMutation } = useApiSend<
    { message: string },
    { tlcId: string; chapterId: string }
  >(
    ({ tlcId, chapterId }) => deleteTLCChapter(tlcId, chapterId),
    () => {
      refetch();
    },
    (error: Error) => {
      setChapterError(error.message || 'Failed to delete chapter');
    }
  );

  const handleEdit = () => {
    if (!tlcData) return;
    setIsEditing(true);
    setName(tlcData.name);
    setStatus(tlcData.is_active);
    setNotes(tlcData.notes);
  };

  const handleCancel = () => {
    if (!tlcData) return;
    setIsEditing(false);
    setName(tlcData.name);
    setStatus(tlcData.is_active);
    setNotes(tlcData.notes);
  };

  const handleSave = () => {
    if (!id) return;
    setIsSaving(true);
    setErrorSaving(null);
    const data: TLCPatchRequest = {
      name: name,
      is_active: status,
      notes: notes,
    };
    updateTLCMutation({ id, data });
  };

  const canEdit = (): boolean => {
    const userRole = localStorage.getItem('role');
    return [roles.Admin, roles.Support].includes(userRole);
  };

  const onSavePosition = (data: SaveData) => {
    if (!id) return;
    setIsSaving(true);
    setErrorSaving(null);
    if (data.action === 'newAppointment') {
      //Handle post
      if (!data.id || data.id === 'new') {
        //Handle new appointment
        if (!data.userId || !data.startDate) return;
        const dataToSend: TLCMemberPostRequest = {
          user_id: data.userId,
          start_date: data.startDate,
          is_primary: data.positionId === 'Primary',
        };
        addTLCMemberMutation({ tlcId: id, data: dataToSend });
      } else {
        // Replace member
        if (!data.userId || !data.startDate) return;
        const dataToSend: TLCMemberReplaceRequest = {
          new_user_id: data.userId,
          start_date: data.startDate,
        };
        replaceMemberMutation({
          tlcId: id,
          memberId: data.id,
          data: dataToSend,
        });
      }
    } else if (data.action === 'editDates') {
      //Handle patch
      if (!data.startDate) return;
      const dataToSend: TLCMemberPatchRequest = {
        start_date: data.startDate,
        end_date: data.endDate || null,
      };
      updateTLCMemberMutation({
        tlcId: id,
        memberId: data.id,
        data: dataToSend,
      });
    } else if (data.action === 'removeRecord') {
      //Handle delete
      removeTLCMemberMutation({ tlcId: id, memberId: data.id });
    }
  };

  const handleAddMember = () => {
    setAddMember(true);
    setOpenMemberSearch(true);
  };

  const handleSetMember = (member: User) => {
    setMember(member);
    setOpenMemberSearch(false);
  };

  const handleCancelAddMember = () => {
    setAddMember(false);
    setMember(null);
    setIsPrimary(false);
    setStartDate(dayjs());
  };

  const handleSaveAddMember = () => {
    if (!member || !id || !startDate) return;
    setIsSaving(true);
    const startDateStr = startDate.toISOString();
    const data: TLCMemberPostRequest = {
      user_id: member.id,
      is_primary: isPrimary,
      start_date: startDateStr,
    };
    addTLCMemberMutation({ tlcId: id, data });
  };

  const handleAddChapter = () => {
    setAddChapterModalOpen(true);
    setChapterError(null);
    setSelectedChapterId('');
  };

  const handleAddChapterClose = () => {
    setAddChapterModalOpen(false);
    setSelectedChapterId('');
    setChapterError(null);
  };

  const handleChapterSelect = (chapterId: string | null) => {
    setSelectedChapterId(chapterId || '');
  };

  const handleAddChapterSubmit = () => {
    if (!selectedChapterId || !id) return;
    setIsSubmittingChapter(true);
    setChapterError(null);
    const data: TLCChapterPostRequest = {
      chapter_id: selectedChapterId,
    };
    addTLCChapterMutation({ tlcId: id, data });
  };

  const handleDeleteChapter = (chapterId: string) => {
    if (!id) return;
    if (window.confirm('Are you sure you want to remove this chapter?')) {
      deleteTLCChapterMutation({ tlcId: id, chapterId });
    }
  };

  if (isLoading) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }
  if (!tlcData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} padding={1} paddingTop={5}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title={tlcData.name}
            subheader={tlcData.is_active ? 'Active' : 'Inactive'}
            sx={{ p: 1 }}
          />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              {isEditing ? (
                <>
                  <Grid item xs={12} md={6} padding={1}>
                    <TextField
                      label="Name"
                      value={name}
                      fullWidth={true}
                      size="small"
                      onChange={(e) => setName(e.target.value)}
                      sx={{ textAlign: 'left' }}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6} padding={1}>
                    <Typography variant="body1">
                      <b>{'Name:'}</b> {tlcData.name}
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={6} padding={1}>
                <Typography variant="body1">
                  <b>{'State:'}</b> {tlcData.state}
                </Typography>
              </Grid>
              {isEditing ? (
                <>
                  <Grid item xs={12} md={6} padding={1}>
                    <FormControl size={'small'} fullWidth={true}>
                      <InputLabel id="state-label">Status</InputLabel>
                      <Select
                        label="Status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value === 'true')}
                      >
                        <MenuItem value={'true'}>Active</MenuItem>
                        <MenuItem value={'false'}>Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6} padding={1}>
                    <Typography variant="body1">
                      <b>{'Status:'}</b>{' '}
                      {tlcData.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Grid>
                </>
              )}
              {isEditing ? (
                <>
                  <Grid item xs={12} md={6} padding={1}>
                    <TextField
                      label="Notes"
                      value={notes}
                      fullWidth={true}
                      size="small"
                      multiline={true}
                      rows={4}
                      onChange={(e) => setNotes(e.target.value)}
                      sx={{ textAlign: 'left' }}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} padding={1}>
                    <Typography variant="body1">
                      <b>{'Notes:'}</b> {tlcData.notes}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
          {canEdit() && (
            <CardActions>
              <Grid container>
                {isEditing ? (
                  <>
                    <Grid item xs={12} padding={1} textAlign={'right'}>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={handleCancel}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Cancel sx={{ fontSize: 17, pr: 1 }} />
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleSave}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Save sx={{ fontSize: 17, pr: 1 }} />
                        Save
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} padding={1} textAlign={'right'}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleEdit}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Edit sx={{ fontSize: 17, pr: 1 }} />
                        Edit
                      </Button>
                    </Grid>
                  </>
                )}
                {tlcError && (
                  <Grid item xs={12} padding={1} textAlign={'center'}>
                    <Typography variant="body1" color="error">
                      {tlcError}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardActions>
          )}
        </Card>
      </Grid>
      <Grid item xs={12} lg={6} padding={1}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title={'Contacts'} sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            {tlcData.members.map((contact) => (
              <PositionDisplay
                id={contact.id}
                position={contact.is_primary ? 'Primary' : 'Contact'}
                positionId={contact.is_primary ? 'Primary' : 'Contact'}
                committeeId={null}
                name={contact.name}
                startDate={contact.start_date}
                endDate={contact.end_date || null}
                canEdit={canEdit()}
                isSaving={isSaving}
                errorSavingText={errorSaving}
                onSave={onSavePosition}
                userId={contact.user_id}
                pageFrom={'tlc'}
                key={contact.id}
              />
            ))}
            {tlcData.members.length === 0 && (
              <Grid item xs={12} textAlign={'center'} paddingTop={2}>
                <Typography variant="body1">No contacts assigned</Typography>
              </Grid>
            )}
            {memberError && (
              <Grid item xs={12} padding={1} textAlign={'center'}>
                <Typography variant="body1" color="error">
                  {memberError}
                </Typography>
              </Grid>
            )}
            {addMember && (
              <>
                <Grid item xs={12} padding={1}>
                  <Divider />
                </Grid>
                <Grid item xs={12} padding={1} textAlign={'center'}>
                  <Typography variant="body1">New Contact</Typography>
                </Grid>
                <Grid item xs={12} paddingLeft={1}>
                  <Typography variant="body1">
                    <b>{'Name:'}</b>{' '}
                    {member ? (
                      `${member.first_name} ${member.last_name}`
                    ) : (
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => setOpenMemberSearch(true)}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Add sx={{ fontSize: 17, pr: 1 }} />
                        Search for Member
                      </Button>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} paddingLeft={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isPrimary}
                        onChange={(e) => setIsPrimary(e.target.checked)}
                      />
                    }
                    label="Primary"
                  />
                </Grid>
                <Grid item xs={12} paddingLeft={1} paddingTop={1}>
                  <DateFilter
                    label="Start Date"
                    filterValue={startDate}
                    updateFilters={setStartDate}
                    disabled={false}
                  />
                </Grid>
              </>
            )}
          </CardContent>
          {canEdit() && (
            <CardActions>
              <Grid container>
                {addMember ? (
                  <>
                    <Grid item xs={12} padding={1} textAlign={'center'}>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={handleCancelAddMember}
                        sx={{ fontWeight: 'bold', mr: 1 }}
                        size="small"
                      >
                        <Cancel sx={{ fontSize: 17, pr: 1 }} />
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleSaveAddMember}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                        disabled={!member || !startDate || isSaving}
                      >
                        <Save sx={{ fontSize: 17, pr: 1 }} />
                        Save Contact
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} padding={1} textAlign={'center'}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleAddMember}
                        sx={{ fontWeight: 'bold' }}
                        size="small"
                      >
                        <Add sx={{ fontSize: 17, pr: 1 }} />
                        Add Contact
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardActions>
          )}
        </Card>
      </Grid>
      <Grid item xs={12} md={6} padding={1}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title={'Local Chapters'}
            sx={{ p: 1 }}
            action={
              canEdit() && (
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleAddChapter}
                  size="small"
                  sx={{ backgroundColor: 'blue', color: 'white' }}
                >
                  Add Chapter
                </Button>
              )
            }
          />
          <CardContent sx={{ p: 1 }}>
            <Grid container>
              {tlcData.chapters.map((localChapter) => (
                <Grid item xs={12} padding={1} key={localChapter.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body1">
                      <b>{'Name:'}</b> {localChapter.chapter_name}
                    </Typography>
                    {canEdit() && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteChapter(localChapter.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
              ))}
              {tlcData.chapters.length === 0 && (
                <Grid item xs={12} textAlign={'center'} paddingTop={2}>
                  <Typography variant="body1">
                    No local chapters assigned
                  </Typography>
                </Grid>
              )}
            </Grid>
            {chapterError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {chapterError}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>
      <UserSearchModal
        modalOpen={openMemberSearch}
        setUser={handleSetMember}
        setModalOpen={setOpenMemberSearch}
        chapter={null}
        spd={null}
        activeMembers={true}
        userObjectRet={true}
        allowSetToMe={true}
      />

      {/* Add Chapter Modal */}
      <Dialog
        open={addChapterModalOpen}
        onClose={handleAddChapterClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Chapter to TLC</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {chapterError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {chapterError}
              </Alert>
            )}
            <ChapterSelector
              chapter={selectedChapterId}
              handleChapterSelect={handleChapterSelect}
              label="Select Chapter"
              all={true}
              spd={null}
              reportId={id}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddChapterClose}>Cancel</Button>
          <Button
            onClick={handleAddChapterSubmit}
            variant="contained"
            disabled={!selectedChapterId || isSubmittingChapter}
            sx={{ backgroundColor: 'blue' }}
          >
            {isSubmittingChapter ? 'Adding...' : 'Add Chapter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default TLCDetail;
